import {
  BAYS,
  BTSessionStatus,
  ClinicalRelationships,
  Roles,
  SessionNoteSignatures,
} from '../../../../constants';
import { areUserRolesIn } from '../../../../helpers';
import {
  getSessionStartDateTime,
  isClientAssignmentActiveAtDateTime,
} from '../../../../components/layout/utils/clientCohortAssignments';
import {
  BCBA_ALLOWED_53_NOTES_BAYS,
  DeliveryMethods,
  IN_PERSON_OPTION,
  TELEHEALTH_OPTION,
} from '../constants';
import { fetchClientProviderAssignments } from '../services/general';

import type { Moment } from 'moment';
import type {
  IClientAssignment,
  IProviderAssignment,
  ISelectOption,
} from '../../../../utils/types';
import type { IGuardianVerificationState, IUpdatedDeliveryMethodParams } from '../types';

function isBcbaProviderAssignment(assignment: IProviderAssignment): boolean {
  const clientRelationship = assignment.clinical_relationship;
  return (
    clientRelationship === ClinicalRelationships.HomeBcba ||
    clientRelationship === ClinicalRelationships.BobbingBcba ||
    clientRelationship === ClinicalRelationships.SubstituteBCBA
  );
}

export async function getNumberOfBcbaProviderAssignments(
  clientId: number | null,
  getAccessTokenSilently: () => Promise<string>,
): Promise<number> {
  if (!clientId) return 0;
  const assignments = await fetchClientProviderAssignments({
    clientId: clientId,
    getAccessTokenSilently: getAccessTokenSilently,
  });
  if (!assignments) return 0;

  const bcbaAssignments = assignments.filter(isBcbaProviderAssignment);
  return bcbaAssignments.length;
}

export function getShowBcbaAttendanceField(
  bay6AssignmentActive: IClientAssignment | undefined,
  areOtherBcbaAssignmentsPresent: boolean,
): boolean {
  if (!bay6AssignmentActive) {
    return true;
  }
  return areOtherBcbaAssignmentsPresent;
}

export function normalizeCohortName(cohortName: string | undefined): string | undefined {
  return cohortName ? cohortName.toUpperCase().trim() : undefined;
}

export function getDeliveryMethodOptions(
  userRoles: Roles[],
  cohortName: string | undefined | null,
): ISelectOption[] {
  const isVbt = areUserRolesIn(userRoles, [Roles.Vbt]);
  const isBcba = areUserRolesIn(userRoles, [Roles.Bcba]);
  const isBay7 = cohortName?.toUpperCase() === BAYS.BAY_7;

  if (isVbt) {
    return isBay7 ? [TELEHEALTH_OPTION, IN_PERSON_OPTION] : [TELEHEALTH_OPTION];
  }

  if (isBcba) {
    return [TELEHEALTH_OPTION];
  }

  return [IN_PERSON_OPTION];
}

export function getDeliveryMethod(cohortName: string, userRoles: Roles[]) {
  let newDeliveryMethod = DeliveryMethods.IN_PERSON;
  if (cohortName === BAYS.BAY_7 && areUserRolesIn(userRoles, [Roles.Vbt])) {
    newDeliveryMethod = DeliveryMethods.IN_PERSON;
  } else if (areUserRolesIn(userRoles, [Roles.Bcba, Roles.Vbt])) {
    newDeliveryMethod = DeliveryMethods.TELEHEALTH;
  }
  return newDeliveryMethod;
}

export function getUpdatedDeliveryMethod({
  cohortName,
  userRoles,
  currentDeliveryMethod,
  sessionId,
}: IUpdatedDeliveryMethodParams): DeliveryMethods | null {
  const normalizedCohortName = normalizeCohortName(cohortName ?? undefined);
  if (!normalizedCohortName || !userRoles) return null;

  const nextDeliveryMethod = getDeliveryMethod(normalizedCohortName, userRoles);
  const allowedDeliveryMethodOptions = getDeliveryMethodOptions(userRoles, normalizedCohortName);
  const isCurrentDeliveryMethodAllowed = allowedDeliveryMethodOptions.some(
    (option) => option.value === currentDeliveryMethod,
  );
  const isExistingSession = Boolean(sessionId);

  if (isExistingSession && currentDeliveryMethod && isCurrentDeliveryMethodAllowed) {
    return null;
  }

  if (
    (!currentDeliveryMethod || !isCurrentDeliveryMethodAllowed || !isExistingSession) &&
    nextDeliveryMethod !== currentDeliveryMethod
  ) {
    return nextDeliveryMethod;
  }

  return null;
}
export function getCurrentClientCohortAssignments(
  clientCohortAssignments: IClientAssignment[] | undefined | null,
  sessionStartDate: Moment | null,
  timeStartValue: Moment | null,
): IClientAssignment | undefined {
  if (!sessionStartDate || !timeStartValue) return undefined;
  const sessionDateTime = getSessionStartDateTime(sessionStartDate, timeStartValue);
  return clientCohortAssignments?.find((assignment) =>
    isClientAssignmentActiveAtDateTime(assignment, sessionDateTime),
  );
}

export function getActiveBayAssignmentAtDateTime(
  assignments: IClientAssignment[],
  bayName: string,
  sessionStartDate: Moment | null,
  timeStartValue: Moment | null,
): IClientAssignment | undefined {
  if (!sessionStartDate || !timeStartValue) return undefined;
  const sessionDateTime = getSessionStartDateTime(sessionStartDate, timeStartValue);
  return assignments
    .filter((assignment) => assignment.cohort?.name?.toUpperCase() === bayName)
    .find((assignment) => isClientAssignmentActiveAtDateTime(assignment, sessionDateTime));
}

export function shouldDisableSessionStartByClientBay(
  clientCohortAssignments: IClientAssignment[] | undefined | null,
  userRoles: Roles[] | undefined | null,
  sessionStartDate: Moment | null,
  timeStartValue: Moment | null,
): boolean {
  if (
    !clientCohortAssignments ||
    !areUserRolesIn(userRoles ?? [], [Roles.Bcba]) ||
    !sessionStartDate ||
    !timeStartValue
  ) {
    return false;
  }
  const disableSessionStartByClientBay = !BCBA_ALLOWED_53_NOTES_BAYS.some((bay) => {
    return getActiveBayAssignmentAtDateTime(
      clientCohortAssignments,
      bay,
      sessionStartDate,
      timeStartValue,
    );
  });
  return disableSessionStartByClientBay;
}

export function getSignatureDialogHeaderText(
  isPRBT: boolean,
  isBay7Active: boolean,
  deliveryMethod: DeliveryMethods | undefined,
  sessionStatus: BTSessionStatus | undefined,
  signatureStep: SessionNoteSignatures,
): string {
  if (
    isBay7Active &&
    deliveryMethod === DeliveryMethods.IN_PERSON &&
    signatureStep === SessionNoteSignatures.ParentSignature
  ) {
    return 'Parent Signature';
  }

  if (!isPRBT || sessionStatus !== BTSessionStatus.EditsRequested) {
    return 'Signature';
  }

  return `Signature ${signatureStep === SessionNoteSignatures.BtSignature ? '1' : '2'} of 2`;
}

export function getGuardianAccessTokenSilently(
  guardianVerification: IGuardianVerificationState | null,
  selectedParentEmail: string,
): string {
  if (
    !guardianVerification ||
    guardianVerification.email !== selectedParentEmail ||
    guardianVerification.expiresAt <= Date.now()
  ) {
    throw new Error('Guardian verification expired. Please verify again.');
  }

  return guardianVerification.token;
}
